import { z } from 'zod'

import {
  JobDetailQuery,
  JobLocationType,
  JobSalaryPeriod,
  JobType,
  UpsertJobRequest,
} from '@/_gql/graphql'

import { formatDateTimeServer, normalizeEnum } from '@/lib/utils/utils'

export interface FormDto extends z.infer<typeof FormDto> {}
export const FormDto = z
  .object({
    title: z.string().trim().min(1, { message: 'Title is required' }),
    job_company_id: z.coerce
      .number({ message: 'Company is required' })
      .positive({ message: 'Company is required' }),
    // salary: z.coerce
    //   .number({ message: 'Sallary is required' })
    //   .min(1, { message: 'Sallary is required' }),
    min: z.coerce.number().min(1, { message: 'Minimum Salary is required' }),
    max: z.coerce.number().min(1, { message: 'Maximum Salary is required' }),
    salary_currency: z.string().optional(),
    salary_period: z.nativeEnum(JobSalaryPeriod).optional(),
    description: z
      .string({ message: 'Description is required' })
      .trim()
      .min(1, { message: 'Description is required' }),
    type: z.nativeEnum(JobType),
    location_type: z.nativeEnum(JobLocationType),
    is_active: z.boolean(),
    location: z.object({
      city: z.object({
        // id: z.string().min(1, { message: 'Required' }),
        id: z.string().optional(),

        name: z.string(),
      }),
      province: z.object({
        // id: z.string().min(1, { message: 'Required' }),
        id: z.string().optional(),
        name: z.string(),
      }),
      country: z
        .object(
          {
            // id: z.string().min(1, { message: 'Required' }),
            id: z.string().optional(),

            name: z.string(),
          }
          // { message: 'Country is required' }
        )
        .optional(),
    }),
    skill_ids: z
      .array(
        z.object({
          id: z.coerce.number().min(1),
          name: z.string().trim(),
        })
      )
      .min(1, { message: 'Skill is required' }),
    tag_ids: z.coerce
      .number({ message: 'Tag is required' })
      .positive({ message: 'Tag is required' }),
    category_ids: z.coerce
      .number({ message: 'Category is required' })
      .positive({ message: 'Category is required' }),
    job_selection_phase: z.array(
      z.object({
        id: z.number().min(1).optional(),
        created_at: z.string().trim().optional(),
        phase_number: z.number().min(1),
        master_job_selection_phase_id: z.number().min(1),
        required_application_document: z.array(z.string().trim()),
        phase: z.string().trim().optional(),
      })
    ),
    is_applied: z.boolean().optional(),
  })
  .superRefine((arg, ctx) => {
    if (arg.min > arg.max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['min'],
        message: 'Minimum Salary cannot be greater than Maximum Salary',
      })
    }
  })

export function getDefaultValues(
  initial?: JobDetailQuery['getJobDetail']['data']
): FormDto {
  const default_job_selection_phase =
    initial?.job_selection_phase && initial?.job_selection_phase.length > 0
      ? initial?.job_selection_phase
      : []
  return {
    title: initial?.title || '',
    job_company_id: initial?.job_company?.id || undefined!,
    // salary: initial?.salary || ('' as unknown as number),
    min: initial?.min || ('' as unknown as number),
    max: initial?.max || ('' as unknown as number),
    salary_currency: initial?.salary_currency || '',
    salary_period:
      normalizeEnum(initial?.salary_period) || JobSalaryPeriod.Hourly,
    description: initial?.description || '',
    type: normalizeEnum(initial?.type) || JobType.Contract,
    location_type:
      normalizeEnum(initial?.location_type) || JobLocationType.OnSite,
    is_active: initial?.is_active || false,
    location: {
      city: {
        id: initial?.city.id || '',
        name: initial?.city.name || '',
      },
      province: {
        id: initial?.province.id || '',
        name: initial?.province.name || '',
      },
      country: {
        id: initial?.country.id || '',
        name: initial?.country.name || '',
      },
    },
    skill_ids:
      initial?.job_skills?.map((x) => ({ id: x.id, name: x.name })) || [],
    tag_ids: initial?.tags?.[0]?.id || null!,
    category_ids: initial?.categories?.[0]?.id || undefined!,
    job_selection_phase: default_job_selection_phase.map((phase, index) => ({
      id: phase.id ?? undefined,
      phase_number: phase.phase_number,
      master_job_selection_phase_id: phase.master_job_selection_phase_id,
      phase: phase?.master_job_selection_phase.phase,
      required_application_document: phase.required_application_document.filter(
        (doc): doc is string => doc !== null
      ),
      created_at: phase.created_at ?? undefined,
    })),
    is_applied: initial?.is_applied || false,
  }
}

export function toPayload(
  data: FormDto,
  meta?: { id: number; created_at: string }
): UpsertJobRequest {
  const { location, job_selection_phase, is_applied, ...rest } = data
  return {
    ...rest,
    // TODO: temporary phase disabled
    // job_selection_phase: [
    //   {
    //     // id: 1,
    //     // created_at: '2024-12-23 13:57:56.403',
    //     phase_number: 1,
    //     master_job_selection_phase_id: 1,
    //     required_application_document: ['user_health', 'user_family'],
    //   },
    // ],
    job_selection_phase: job_selection_phase.map((phase, index) => ({
      ...(phase.id ? { id: phase.id } : {}),
      ...(phase.created_at ? { created_at: phase.created_at } : {}),
      phase_number: index + 1,
      master_job_selection_phase_id: phase.master_job_selection_phase_id,
      required_application_document: phase.required_application_document,
    })),

    location_city_id: location.city.id,
    location_province_id: location.province.id,
    location_country_id: location.country?.id,
    // TODO: Currency not available yet
    salary_currency: data.salary_currency || '',
    salary_period: data.salary_period || JobSalaryPeriod.Hourly,
    category_ids: data.category_ids ? [data.category_ids] : [],
    tag_ids: data.tag_ids ? [data.tag_ids] : [],
    skill_ids: data.skill_ids.map((x) => x.id),
    id: meta?.id,
    created_at: meta?.created_at
      ? formatDateTimeServer(new Date(meta.created_at))
      : undefined,
  }
}
